import React from 'react';
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Alert,
  AlertTitle,
  Stack,
  Typography,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import RefreshIcon from '@mui/icons-material/Refresh';
import SupportIcon from '@mui/icons-material/Support';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/rootReducerType';
import { getUserDisplayName } from 'src/components/NewRenterInterface/utils/NewRentersInterfaceUtils';

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
  expanded: boolean;
}

interface Props {
  children: React.ReactNode;
  fullName?: string;
}

class RenterContentErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      expanded: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error(
      'RenterContentErrorBoundary caught an error:',
      error,
      errorInfo
    );
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  handleOpenSupport = (): void => {
    window.location.href = 'mailto:support@prontohousing.com';
  };

  handleCopyError = (): void => {
    const { error, errorInfo } = this.state;
    const text = `${error?.toString() ?? ''}\n${
      errorInfo?.componentStack ?? ''
    }`;
    navigator.clipboard.writeText(text).catch((err) => {
      console.error('Failed to copy error:', err);
    });
  };

  handleToggleExpanded = (): void => {
    this.setState((prev) => ({ expanded: !prev.expanded }));
  };

  render(): React.ReactNode {
    if (this.state.hasError) {
      const { error, errorInfo, expanded } = this.state;
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            p: 4,
          }}
        >
          <Stack
            spacing={3}
            alignItems="center"
            sx={{ maxWidth: 700, width: '100%' }}
          >
            <BugReportIcon sx={{ fontSize: 56, color: 'error.main' }} />
            {this.props.fullName && (
              <Typography
                variant="body1"
                color="text.secondary"
                textAlign="center"
              >
                Hello {this.props.fullName}
              </Typography>
            )}
            <Typography variant="h5" fontWeight={600} textAlign="center">
              We've encountered an error
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              An unexpected error occurred. You can try reloading the page or
              contact support if the problem persists.
            </Typography>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleReload}
              >
                <span>Reload Page</span>
              </Button>
              <Button
                variant="outlined"
                startIcon={<SupportIcon />}
                onClick={this.handleOpenSupport}
              >
                <span>Get Help</span>
              </Button>
            </Stack>
            {error && (
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  textAlign: 'left',
                  '& .MuiAlert-message': { width: '100%' },
                }}
                action={
                  <IconButton
                    size="small"
                    onClick={this.handleToggleExpanded}
                    title="Toggle error details"
                    sx={{
                      transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.2s',
                      color: 'error.main',
                    }}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                }
              >
                <Stack
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <AlertTitle sx={{ fontWeight: 600 }}>
                    Error Details
                  </AlertTitle>
                  <Button
                    size="small"
                    startIcon={<ContentCopyIcon fontSize="small" />}
                    onClick={this.handleCopyError}
                    sx={{ mb: 1, color: 'text.secondary' }}
                  >
                    <span>Copy Error</span>
                  </Button>
                </Stack>
                <Collapse in={expanded}>
                  <Box
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      backgroundColor: 'background.paper',
                      p: 1.5,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      maxHeight: 300,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      m: 0,
                    }}
                  >
                    {error.toString()}
                    {errorInfo?.componentStack ?? ''}
                  </Box>
                </Collapse>
              </Alert>
            )}
          </Stack>
        </Box>
      );
    }

    return this.props.children;
  }
}

function RenterContentErrorBoundaryWithAuth({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );
  return (
    <RenterContentErrorBoundary fullName={getUserDisplayName(user)}>
      {children}
    </RenterContentErrorBoundary>
  );
}

export default RenterContentErrorBoundaryWithAuth;
